import React, { useState } from 'react';
import type { IHeaderGroupParams } from 'ag-grid-community';
import ExpandChevronCell from '../cells/ExpandChevronCell';

/** Params a caller may add via `headerGroupComponentParams` on the column-group definition. */
export interface GroupHeaderParams extends IHeaderGroupParams {
  /**
   * Optional control rendered at the right-hand end of the group-header bar (e.g. a bulk action that
   * belongs to this group of columns). Passed as a component rather than an element so it re-renders
   * with its own context when AG-Grid recreates the header cell.
   */
  actionsComponent?: React.ComponentType;
}

/**
 * Column-group header with a horizontal expand/collapse toggle that reuses the shared
 * `ExpandChevronCell` glyph in controlled mode, so a group toggle and a row toggle look
 * identical. The group is toggled only from this button, so the expanded state is tracked
 * optimistically on click. A caller may also mount a right-aligned action via `actionsComponent`;
 * groups that pass nothing render exactly as before.
 */
const GroupHeader: React.FC<GroupHeaderParams> = ({ columnGroup, setExpanded, displayName, actionsComponent: Actions }) => {
  const [expanded, setExpandedState] = useState<boolean>(columnGroup.isExpanded());
  const expandable = columnGroup.isExpandable();

  const onToggle = (): void => {
    const next = !expanded;
    setExpanded(next);
    setExpandedState(next);
  };

  return (
    <div className="cag-group-header">
      <span className="cag-group-header__label">{displayName}</span>
      {expandable && (
        <ExpandChevronCell
          expanded={expanded}
          onToggle={onToggle}
          expandLabel={`Expand ${displayName}`}
          collapseLabel={`Collapse ${displayName}`}
          className="cag-chevron-toggle cag-group-header__toggle"
        />
      )}
      {Actions && (
        <span className="cag-group-header__actions">
          <Actions />
        </span>
      )}
    </div>
  );
};

export default GroupHeader;
